import type { ModuleStorage } from "@artis/sdk";
import { useCallback, useEffect, useRef } from "react";

import {
  SCRATCHPAD_WORKBOOK_KEY,
  deserializeWorkbook,
  type PersistedWorkbook,
  serializeWorkbook,
  type Sheet,
} from "@package/scratchpad-utils";

const AUTOSAVE_WINDOW_MS = 1000;

// Loads the workbook from storage, if any. Returns null if no workbook is found or if it fails to deserialize.
export async function loadAutosaved(storage: ModuleStorage): Promise<PersistedWorkbook | null> {
  return deserializeWorkbook((await storage.get<string>(SCRATCHPAD_WORKBOOK_KEY)) ?? null);
}

// Hook that autosaves the given workbook to storage, throttled: the first change writes at once, further changes within the window coalesce into one trailing write. Returns a function to mark the current state as persisted (so it won't be re-saved).
export function useAutosave(storage: ModuleStorage, sheets: Sheet[], activeSheetId: string) {
  const lastRef = useRef<string | null>(null);
  const lastWriteAtRef = useRef(Number.NEGATIVE_INFINITY);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);

  // Seed the dedup baseline with the loaded workbook so a fresh open does not re-persist it. Autosave only writes once the user actually changes something.
  const seededRef = useRef(false);
  if (!seededRef.current) {
    seededRef.current = true;
    lastRef.current = serializeWorkbook(sheets, activeSheetId);
  }

  const write = useCallback(
    (json: string) => {
      lastRef.current = json;
      lastWriteAtRef.current = Date.now();
      const send = () => {
        if (lastRef.current !== json) return Promise.resolve();
        return storage.set(SCRATCHPAD_WORKBOOK_KEY, json).catch(() => {
          if (lastRef.current === json) lastRef.current = null;
        });
      };
      const run: Promise<void> = (inFlightRef.current ? inFlightRef.current.then(send) : send()).then(() => {
        if (inFlightRef.current === run) inFlightRef.current = null;
      });
      inFlightRef.current = run;
    },
    [storage],
  );

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const json = pendingRef.current;
    if (json === null) return;
    pendingRef.current = null;
    write(json);
  }, [write]);

  useEffect(() => {
    const json = serializeWorkbook(sheets, activeSheetId);
    if (json === lastRef.current || json === pendingRef.current) return;
    if (timerRef.current !== null) {
      pendingRef.current = json;
      return;
    }
    const remaining = lastWriteAtRef.current + AUTOSAVE_WINDOW_MS - Date.now();
    if (remaining <= 0) {
      write(json);
      return;
    }
    pendingRef.current = json;
    timerRef.current = setTimeout(flush, remaining);
  }, [flush, write, sheets, activeSheetId]);

  useEffect(() => {
    const onPageHide = () => flush();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      flush();
    };
  }, [flush]);

  const markPersisted = useCallback((nextSheets: Sheet[], nextActiveSheetId: string) => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    lastRef.current = serializeWorkbook(nextSheets, nextActiveSheetId);
  }, []);

  return { markPersisted };
}
